import React, { useState } from 'react';
import { MapPin, Navigation, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const POPULAR_CITIES = [
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai',
  'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow',
  'Bhopal', 'Indore', 'Patna', 'Ranchi', 'Guwahati',
  'Bhubaneswar', 'Dehradun', 'Chandigarh', 'Nagpur', 'Raipur',
];

const LocationPrompt = () => {
  const { showLocationPrompt, retryLocation, dismissLocationPrompt, updateCity, locationLoading } = useAuth();
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [customCity, setCustomCity] = useState('');

  if (!showLocationPrompt) return null;

  const handleCitySelect = (city) => {
    updateCity(city);
    dismissLocationPrompt();
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (customCity.trim()) {
      updateCity(customCity.trim());
      dismissLocationPrompt();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="location-prompt-overlay">
      <div className="bg-white w-full max-w-md border-2 border-slate-200 shadow-[6px_6px_0px_#EA580C] animate-scale-in" data-testid="location-prompt-modal">
        {/* Header */}
        <div className="bg-slate-900 p-5 text-center relative">
          <button
            onClick={dismissLocationPrompt}
            className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
            data-testid="location-prompt-close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="h-14 w-14 mx-auto bg-orange-600 flex items-center justify-center mb-3">
            <MapPin className="h-7 w-7 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white tracking-tight">Where are you building?</h2>
          <p className="text-sm text-slate-400 mt-1">We need your location to show nearby suppliers</p>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Option 1: Enable GPS */}
          <Button
            onClick={retryLocation}
            disabled={locationLoading}
            className="w-full h-12 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-none shadow-[3px_3px_0px_#0F172A] hover:shadow-[1px_1px_0px_#0F172A] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-base"
            data-testid="location-enable-gps-btn"
          >
            <Navigation className="h-5 w-5 mr-2" />
            {locationLoading ? 'Detecting Location...' : 'Use My Current Location'}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs uppercase tracking-wider"><span className="bg-white px-3 text-muted-foreground font-bold">or select city</span></div>
          </div>

          {/* Option 2: Pick city */}
          {!showCityPicker ? (
            <Button
              variant="outline"
              onClick={() => setShowCityPicker(true)}
              className="w-full h-11 rounded-none border-2 border-slate-200 font-bold text-slate-700"
              data-testid="location-pick-city-btn"
            >
              Choose Your City <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <div className="space-y-3">
              {/* Quick search */}
              <form onSubmit={handleCustomSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={customCity}
                  onChange={(e) => setCustomCity(e.target.value)}
                  placeholder="Type your city name..."
                  className="flex-1 h-10 px-3 border-2 border-slate-200 bg-orange-50/40 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400 focus:bg-white"
                  autoFocus
                  data-testid="location-city-input"
                />
                <Button type="submit" disabled={!customCity.trim()} className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-none font-bold" data-testid="location-city-submit">
                  Go
                </Button>
              </form>

              {/* Popular cities grid */}
              <div className="grid grid-cols-3 gap-1.5 max-h-44 overflow-y-auto">
                {POPULAR_CITIES.map(city => (
                  <button
                    key={city}
                    onClick={() => handleCitySelect(city)}
                    className="py-2 px-2 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-700 transition-colors text-center truncate"
                    data-testid={`city-option-${city.toLowerCase()}`}
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Skip */}
          <button
            onClick={dismissLocationPrompt}
            className="w-full text-xs text-muted-foreground hover:text-slate-700 transition-colors text-center py-1"
            data-testid="location-skip-btn"
          >
            Skip for now — I'll browse without location
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPrompt;
